/**
 * **Run 19 §9 (DECIDED 20261016, Barna): the mobile keyboard.**
 *
 * Ágó's test of 16 October found that on a phone the header, the active
 * section, the fixed bottom bar and the keyboard together leave almost no
 * room to type: the field being filled in sits in a strip a line or two
 * high, between the sticky header above it and the offer bar below.
 *
 *   > While a text or number field has focus on a narrow screen, the fixed
 *   > bottom bar and any sticky header are hidden. They return when the
 *   > field loses focus. Use `focusin`/`focusout`, with `visualViewport` as
 *   > a check where available. Desktop is unchanged.
 *
 * **How.** One attribute on the document element, `data-typing="true"`, and
 * the stylesheet does the rest — so nothing re-renders while somebody is
 * typing and no React state is written on every keystroke. `focusin` and
 * `focusout` bubble, which is why one listener on the document covers every
 * field on the page, including the ones drawn after it was installed.
 *
 * **Why `visualViewport` is the check.** The width says the bars *could* be
 * in the way; only the visual viewport says the keyboard is actually up.
 * A field can have focus with no keyboard at all — a desktop browser
 * narrowed to phone width, a reader who dismissed the keyboard and left the
 * caret where it was — and hiding the total and the order button in those
 * cases would be a loss for nothing. So where the browser has a visual
 * viewport the bars go only once it is materially shorter than the layout
 * viewport; where it has none, the width alone decides, which is exactly
 * what §9's *where available* allows. A `resize` while the field still has
 * focus re-reads the answer, which is what covers a phone rotated with the
 * keyboard open and a keyboard dismissed without leaving the field.
 *
 * Everything here is pure or takes its document and window as arguments, so
 * the jsdom suite drives it directly.
 */

/** The attribute the stylesheet watches, on `<html>`. */
export const TYPING_ATTRIBUTE = 'data-typing'

/**
 * The one-column breakpoint, which is also where `.offerbar` becomes a fixed
 * bar at all (`styles.css`, `@media(max-width:1020px)`). Above it there is no
 * bottom bar to hide and the header is not in anybody's way, which is what
 * §9's *desktop is unchanged* means.
 */
export const NARROW_MAX_WIDTH = 1020

/**
 * A field somebody types into: a text-like `input` or a `textarea`.
 *
 * A checkbox, a radio, a button, a colour or a file picker opens no keyboard
 * and hiding the bars for one would take the total off the screen for
 * nothing. A `select` is left out for the same reason: its own list covers
 * the page while it is open and closes again on choosing.
 *
 * `date` and `time` are left out too — they open a picker rather than a
 * keyboard on every platform the page is tested on, and the date field in
 * particular is read through a second, visible field that would jump.
 */
const TYPING_INPUT_TYPES: readonly string[] = [
  'text',
  'number',
  'email',
  'tel',
  'search',
  'url',
  'password',
]

export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as { tagName?: string; type?: string; readOnly?: boolean } | null
  if (element === null || typeof element.tagName !== 'string') return false
  const tag = element.tagName.toLowerCase()
  if (tag === 'textarea') return element.readOnly !== true
  if (tag !== 'input') return false
  if (element.readOnly === true) return false
  const type = String(element.type ?? 'text').toLowerCase()
  return TYPING_INPUT_TYPES.includes(type)
}

/**
 * Is this a screen narrow enough for the bars to be in the way?
 *
 * `visualViewport.width` where the browser has one — it is the part of the
 * page the reader can actually see — and `innerWidth` otherwise. Neither is
 * a media query, because the attribute has to be set from script and a
 * `matchMedia` that disagreed with the stylesheet by a pixel would hide the
 * bars at one width and not the other.
 */
export function narrowScreen(view: {
  innerWidth?: number
  visualViewport?: { width?: number } | null
}): boolean {
  const visual = view.visualViewport?.width
  const width = typeof visual === 'number' && visual > 0 ? visual : view.innerWidth
  if (typeof width !== 'number' || !Number.isFinite(width)) return false
  return width <= NARROW_MAX_WIDTH
}

/**
 * How much shorter the visible page has to get before the thing eating it
 * is read as a keyboard. A phone keyboard takes a third of the screen or
 * more; a browser's own toolbar sliding away takes well under this.
 */
export const KEYBOARD_MIN_PX = 120

/**
 * **§9's *with `visualViewport` as a check where available*.**
 *
 * A field can have focus with no keyboard up at all: a desktop browser
 * narrowed to phone width, a phone where the reader dismissed the keyboard
 * and left the caret where it was, or a script that filled the field. In
 * every one of those the bars are not in anybody's way, and hiding the
 * total and the order button would be a loss for nothing.
 *
 * So where the browser has a visual viewport — which is the part of the
 * page the reader can actually see — the bars go only once it is
 * materially shorter than the layout viewport, which is what a keyboard
 * coming up looks like and nothing else does. Where there is none, there
 * is no way to tell, and the width alone decides, exactly as §9's *where
 * available* allows.
 */
export function keyboardIsUp(view: {
  innerHeight?: number
  visualViewport?: { height?: number } | null
}): boolean {
  const visual = view.visualViewport?.height
  const layout = view.innerHeight
  if (typeof visual !== 'number' || typeof layout !== 'number') return true
  if (!Number.isFinite(visual) || !Number.isFinite(layout) || layout <= 0) return true
  return layout - visual >= KEYBOARD_MIN_PX
}

/** §9's whole condition, in one place, so the three callers cannot differ. */
export const barsShouldHide = (
  target: EventTarget | null,
  view: Window | {
    innerWidth?: number
    innerHeight?: number
    visualViewport?: { width?: number; height?: number } | null
  },
): boolean => isTypingTarget(target) && narrowScreen(view) && keyboardIsUp(view)

/** Whether the bars are hidden right now. Read by the tests and by nothing else. */
export const typingHidden = (doc: Document): boolean =>
  doc.documentElement.getAttribute(TYPING_ATTRIBUTE) === 'true'

/**
 * Install the listeners. Returns the function that removes them again, which
 * is what a React effect wants back.
 *
 * `focusout` always clears the attribute, whatever the width: a window
 * resized wide while a field is focused must not leave the bars hidden for
 * the rest of the visit.
 */
export function installKeyboardHide(doc: Document, view: Window): () => void {
  const set = (on: boolean): void => {
    if (on) doc.documentElement.setAttribute(TYPING_ATTRIBUTE, 'true')
    else doc.documentElement.removeAttribute(TYPING_ATTRIBUTE)
  }
  const onFocusIn = (event: Event): void => {
    set(barsShouldHide(event.target, view))
  }
  const onFocusOut = (): void => set(false)
  /* The keyboard coming up is a visual-viewport resize, and a rotation is a
     window one. Either way the question is the same: is the field that has
     focus still on a narrow screen? */
  const onResize = (): void => {
    set(barsShouldHide(doc.activeElement, view))
  }
  doc.addEventListener('focusin', onFocusIn)
  doc.addEventListener('focusout', onFocusOut)
  view.addEventListener('resize', onResize)
  view.visualViewport?.addEventListener('resize', onResize)
  return () => {
    doc.removeEventListener('focusin', onFocusIn)
    doc.removeEventListener('focusout', onFocusOut)
    view.removeEventListener('resize', onResize)
    view.visualViewport?.removeEventListener('resize', onResize)
    set(false)
  }
}
